import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { baseAxios } from "../../utils/config";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Switch,
  Typography,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  AddPhotoAlternateOutlined as AddImagesIcon,
  BrokenImage as BrokenImageIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  FileDownloadOutlined as ExportIcon,
  FilterList as FilterListIcon,
  ImageOutlined as ImageOutlinedIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { colors } from "../../theme/colors";
import ImportProductsDialog from "./ImportProductsDialog";
import ProductImagesDialog from "./ProductImagesDialog";
import {
  applyMasterLookup,
  catalogImageUrl,
  needsBrand,
  needsManufacturer,
  productRefsPayload,
} from "./masterLookupHelpers";
import {
  brandChoices,
  categoryChoices,
  categoryRefPayload,
  manufacturerChoices,
  manufacturerFilterOptions,
  optionLabel,
  resolvePick,
  sortByName,
} from "./productFormHelpers";

function addDollarSign(amount) {
  if (amount == "undefined" || amount == undefined || amount < 0.01) {
    amount = 0.0;
  }
  return `$${amount}`;
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["1", "true", "yes"].includes(value.toLowerCase());
  }
  return false;
}

const MARK_STYLE = {
  backgroundColor: "rgba(197, 160, 111, 0.45)",
  color: "inherit",
  borderRadius: 4,
};

const CONTROL_RADIUS = "6px";

function textCell(content) {
  return (
    <Typography
      component="span"
      fontSize={12}
      sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
    >
      {content}
    </Typography>
  );
}

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, index) =>
    pattern.test(part) ? (
      <mark key={index} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function formatProduct(product) {
  return {
    ...product,
    manufacturer: String(product.manufacturer ?? "").trim(),
    isRetail: normalizeBoolean(product.isRetail),
    price: addDollarSign(product.price),
    msrp: addDollarSign(product.msrp),
    distributor:
      product.reseller_company_name ||
      product.distributor ||
      "",
    ozWeight:
      product.weight == null || product.weight === ""
        ? ""
        : Number(product.weight).toFixed(2),
  };
}

function parseMoney(value) {
  if (value == null || value === "") return "";
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(num) ? "" : num;
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

const EMPTY_PRODUCT_FORM = {
  name: "",
  manufacturerId: null,
  // Pending names, from the master catalog lookup (JD-422) or typed into the
  // picker (JD-423): set only while the organization has no matching row
  // yet; the API creates it on save.
  manufacturerName: null,
  brandId: null,
  brandName: null,
  barcode: "",
  categoryId: null,
  categoryName: null,
  distributorId: null,
  ozSize: "",
  price: "",
  msrp: "",
  isRetailProduct: false,
  imageDeleted: false,
};

function alphanumericBarcode(value) {
  return String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");
}

function productRowToFormValues(row) {
  return {
    name: row.name || "",
    manufacturerId: row.manufacturer_id ?? null,
    manufacturerName: null,
    brandId: row.brand_id ?? null,
    brandName: null,
    barcode: alphanumericBarcode(row.barcode),
    categoryId: row.category_id ?? null,
    categoryName: null,
    distributorId: row.reseller_id ?? row.default_reseller_id ?? null,
    ozSize: row.ozWeight ?? row.weight ?? "",
    price: parseMoney(row.price),
    msrp: parseMoney(row.msrp),
    isRetailProduct: normalizeBoolean(row.isRetail),
    imageDeleted: false,
  };
}

function productMatchesSearch(product, query) {
  const normalizedQuery = String(query ?? "").trim().toLowerCase();
  if (!normalizedQuery) return true;

  const textBlob = [
    product.manufacturer,
    product.name,
    product.brand,
    product.category,
    product.type,
    product.barcode,
    product.distributor,
    product.ozWeight,
    product.price,
    product.msrp,
    product.isRetail ? "retail yes" : "not retail no",
    product.product_image ? "has image image" : "no image missing image",
  ]
    .map((value) => String(value ?? "").toLowerCase())
    .join(" ");

  const digitBlob = [product.barcode, product.ozWeight]
    .map((value) => String(value ?? "").replace(/\D/g, ""))
    .join(" ");
  const queryDigits = normalizedQuery.replace(/\D/g, "");

  if (textBlob.includes(normalizedQuery)) return true;
  if (queryDigits.length >= 2 && digitBlob.includes(queryDigits)) return true;

  return normalizedQuery
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => {
      const tokenDigits = token.replace(/\D/g, "");
      if (tokenDigits.length >= 2 && digitBlob.includes(tokenDigits)) {
        return true;
      }
      return textBlob.includes(token);
    });
}

function compareProductRows(a, b, field) {
  const valueForSort = (row) => {
    if (field === "product_id") return Number(row.product_id) || 0;
    if (field === "price" || field === "msrp") {
      return Number(String(row[field] ?? "").replace(/[^0-9.-]/g, "")) || 0;
    }
    if (field === "ozWeight") return Number(row.ozWeight) || 0;
    if (field === "isRetail") return row.isRetail ? 1 : 0;
    if (field === "distributor") return String(row.distributor ?? "");
    return String(row[field] ?? "");
  };

  const left = valueForSort(a);
  const right = valueForSort(b);
  if (typeof left === "number" && typeof right === "number") {
    return left - right || (Number(a.product_id) || 0) - (Number(b.product_id) || 0);
  }
  return (
    String(left).localeCompare(String(right), undefined, { sensitivity: "base" }) ||
    (Number(a.product_id) || 0) - (Number(b.product_id) || 0)
  );
}

function RetailFilter({
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  ariaLabel = "Filter by retail type",
  yesLabel = "Yes",
  noLabel = "No",
}) {
  const active = value.length > 0;
  const options = [
    { value: true, label: yesLabel },
    { value: false, label: noLabel },
  ];

  const toggleValue = (optionValue) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((item) => item !== optionValue)
        : [...value, optionValue]
    );
  };

  return (
    <Box
      component="span"
      onMouseDown={(event) => event.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        ml: 0.25,
      }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        color={active ? "primary" : "inherit"}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl((prev) =>
            prev === event.currentTarget ? null : event.currentTarget
          );
        }}
        sx={{
          p: 0.35,
          color: active ? colors.gold : "rgba(255,255,255,0.75)",
          "&:hover": { color: active ? colors.gold : "common.white" },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
        PaperProps={{
          sx: {
            minWidth: 180,
            py: 0.5,
            borderRadius: CONTROL_RADIUS,
          },
        }}
      >
        <MenuItem
          dense
          sx={{ minHeight: 32, py: 0.25 }}
          onClick={() => {
            onChange([]);
          }}
        >
          <ListItemText
            primary="All"
            primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
          />
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.label}
            dense
            selected={value.includes(option.value)}
            onClick={() => toggleValue(option.value)}
            sx={{ minHeight: 32, py: 0.25 }}
          >
            <Checkbox
              size="small"
              disableRipple
              checked={value.includes(option.value)}
              sx={{
                p: 0.25,
                mr: 0.75,
                color: colors.gold,
                "&.Mui-checked": { color: colors.gold },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            />
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function FieldFilter({
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  ariaLabel,
  allLabel,
}) {
  const active = value.length > 0;
  const [filterSearch, setFilterSearch] = useState("");
  const filteredOptions = useMemo(() => {
    const query = filterSearch.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      String(option).toLowerCase().includes(query)
    );
  }, [filterSearch, options]);

  useEffect(() => {
    if (!anchorEl) setFilterSearch("");
  }, [anchorEl]);

  const toggleOption = (option) => {
    onChange(
      value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option]
    );
  };

  return (
    <Box
      component="span"
      onMouseDown={(event) => event.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        ml: 0.25,
      }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        color={active ? "primary" : "inherit"}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl((prev) =>
            prev === event.currentTarget ? null : event.currentTarget
          );
        }}
        sx={{
          p: 0.35,
          color: active ? colors.gold : "rgba(255,255,255,0.75)",
          "&:hover": { color: active ? colors.gold : "common.white" },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 260,
            maxHeight: 360,
            py: 0.5,
            borderRadius: CONTROL_RADIUS,
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.75,
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search options..."
            value={filterSearch}
            onChange={(event) => setFilterSearch(event.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: CONTROL_RADIUS,
                fontSize: "0.82rem",
                height: 34,
              },
            }}
          />
        </Box>
        <MenuItem
          dense
          sx={{ minHeight: 32, py: 0.25 }}
          onClick={() => {
            onChange([]);
          }}
        >
          <ListItemText
            primary={allLabel}
            primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
          />
        </MenuItem>
        {filteredOptions.length === 0 ? (
          <MenuItem disabled dense sx={{ minHeight: 32, py: 0.25 }}>
            <ListItemText
              primary="No matching options"
              primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
            />
          </MenuItem>
        ) : null}
        {filteredOptions.map((option) => (
          <MenuItem
            key={option}
            dense
            selected={value.includes(option)}
            onClick={() => toggleOption(option)}
            sx={{ minHeight: 32, py: 0.25 }}
          >
            <Checkbox
              size="small"
              disableRipple
              checked={value.includes(option)}
              sx={{
                p: 0.25,
                mr: 0.75,
                color: colors.gold,
                "&.Mui-checked": { color: colors.gold },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            />
            <ListItemText
              primary={option}
              primaryTypographyProps={{ sx: { fontSize: "0.82rem" } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function ProductImage({ value, name, onOpen }) {
  const [failed, setFailed] = useState(false);
  const showPlaceholder = !value || failed;

  useEffect(() => {
    setFailed(false);
  }, [value]);

  return (
    <Box
      component={!showPlaceholder ? "button" : "span"}
      type={!showPlaceholder ? "button" : undefined}
      onClick={!showPlaceholder ? onOpen : undefined}
      title={!showPlaceholder ? "View product image" : "Missing or invalid image"}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 44,
        height: 44,
        border: "1px solid",
        borderColor: (theme) =>
          !showPlaceholder
            ? theme.palette.mode === "dark"
              ? "#45454c"
              : colors.black
            : "divider",
        borderRadius: CONTROL_RADIUS,
        bgcolor: (theme) =>
          !showPlaceholder
            ? "white"
            : theme.palette.mode === "dark"
              ? "#1b1b1e"
              : "grey.100",
        color: (theme) =>
          theme.palette.mode === "dark" ? "#8a8a8a" : "grey.400",
        overflow: "hidden",
        position: "relative",
        p: 0,
        cursor: !showPlaceholder ? "pointer" : "default",
        transition: "border-color 140ms ease",
        "&:hover": {
          borderColor: !showPlaceholder ? "grey.900" : "divider",
        },
        "&:hover .product-image-hover": {
          opacity: !showPlaceholder ? 1 : 0,
        },
      }}
    >
      {!showPlaceholder ? (
        <>
          <Box
            component="img"
            src={value}
            alt={name || "Product"}
            onError={() => setFailed(true)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
          <Box
            className="product-image-hover"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0, 0, 0, 0.55)",
              color: "white",
              opacity: 0,
              transition: "opacity 140ms ease",
            }}
          >
            <VisibilityIcon sx={{ fontSize: 24 }} />
          </Box>
        </>
      ) : (
        <BrokenImageIcon sx={{ fontSize: 22 }} />
      )}
    </Box>
  );
}

function ProductCell({ row, query, onImageOpen }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.25 }}>
      <ProductImage
        value={row.product_image}
        name={row.name}
        onOpen={() =>
          onImageOpen({
            src: row.product_image,
            name: row.name,
          })
        }
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          fontWeight={600}
          fontSize={12}
          sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
        >
          {row.name ? <HighlightText text={row.name} query={query} /> : "-"}
        </Typography>
        {row.barcode ? (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ display: "block", mt: 0.25, lineHeight: 1.3 }}
          >
            <HighlightText text={row.barcode} query={query} />
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function ProductViewSwitcher({
  isMissingInfo,
  productsCount,
  missingInfoCount,
  onChange,
}) {
  const options = [
    {
      key: "all",
      active: !isMissingInfo,
      label: "All",
      count: productsCount,
      onClick: () => onChange(false),
    },
    {
      key: "needs-info",
      active: isMissingInfo,
      label: "Needs Info",
      count: missingInfoCount,
      onClick: () => onChange(true),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 40,
        p: 0.35,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: CONTROL_RADIUS,
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "#141416" : "grey.100",
      }}
    >
      {options.map((option) => (
        <Box
          key={option.key}
          component="button"
          type="button"
          onClick={option.onClick}
          aria-pressed={option.active}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            height: "100%",
            px: 1.2,
            py: 0,
            border: 0,
            borderRadius: CONTROL_RADIUS,
            cursor: "pointer",
            bgcolor: option.active ? colors.black : "transparent",
            color: option.active ? colors.gold : "text.secondary",
            boxShadow: "none",
            transition: "all 140ms ease",
            "&:hover": {
              bgcolor: option.active
                ? colors.black
                : (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.04)",
            },
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: "0.78rem", fontWeight: 700, lineHeight: 1 }}
          >
            {option.label}
          </Typography>
          <Box
            component="span"
            sx={{
              minWidth: 22,
              height: 20,
              px: 0.75,
              borderRadius: CONTROL_RADIUS,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.72rem",
              fontWeight: 800,
              bgcolor: (theme) =>
                option.active
                  ? colors.gold
                  : theme.palette.mode === "dark"
                    ? "#2a2a2e"
                    : "grey.300",
              color: option.active ? colors.black : "text.secondary",
            }}
          >
            {option.count}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ProductDialog({
  open,
  productId,
  initialProduct,
  brands,
  manufacturers,
  categories,
  distributors,
  formOptionsLoading,
  onClose,
  onSaved,
  onImageOpen,
}) {
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_PRODUCT_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [existingImage, setExistingImage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  // Master catalog lookup (JD-422)
  const [barcodeTaken, setBarcodeTaken] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const lastLookedUpBarcode = useRef(null);
  // Latest chosen file, so a lookup that returns while the user is picking
  // a picture does not replace it.
  const imageFileRef = useRef(null);
  imageFileRef.current = imageFile;

  const isEdit = productId !== null && productId !== undefined;
  const busy = saving || formOptionsLoading;

  // A manufacturer, brand or category the organization does not have yet
  // (named by the master lookup, or typed into the picker) is shown as a
  // "(new)" value so the user sees what will be created on save. The pickers
  // list every row as options (MUI wants the value among them) and narrow
  // them per keystroke through filterOptions, which also adds the
  // `Add "..."` entry (productFormHelpers).
  const pendingManufacturer = useMemo(
    () =>
      !hasValue(formValues.manufacturerId) && formValues.manufacturerName
        ? {
            manufacturer_id: null,
            manufacturer_name: formValues.manufacturerName,
            pending: true,
          }
        : null,
    [formValues.manufacturerId, formValues.manufacturerName]
  );

  const manufacturerOptions = useMemo(
    () => (pendingManufacturer ? [pendingManufacturer, ...manufacturers] : manufacturers),
    [manufacturers, pendingManufacturer]
  );

  const selectedManufacturer = useMemo(
    () =>
      pendingManufacturer ||
      manufacturers.find(
        (manufacturer) =>
          manufacturer.manufacturer_id === formValues.manufacturerId
      ) ||
      null,
    [formValues.manufacturerId, manufacturers, pendingManufacturer]
  );

  const pendingBrand = useMemo(
    () =>
      !hasValue(formValues.brandId) && formValues.brandName
        ? {
            brand_id: null,
            brand_name: formValues.brandName,
            manufacturer_id: formValues.manufacturerId ?? null,
            pending: true,
          }
        : null,
    [formValues.brandId, formValues.brandName, formValues.manufacturerId]
  );

  const brandOptions = useMemo(
    () => (pendingBrand ? [pendingBrand, ...brands] : brands),
    [brands, pendingBrand]
  );

  const selectedBrand = useMemo(
    () =>
      pendingBrand ||
      brands.find((brand) => brand.brand_id === formValues.brandId) ||
      null,
    [brands, formValues.brandId, pendingBrand]
  );

  const pendingCategory = useMemo(
    () =>
      !hasValue(formValues.categoryId) && formValues.categoryName
        ? {
            category_id: null,
            category_name: formValues.categoryName,
            brand_id: formValues.brandId ?? null,
            pending: true,
          }
        : null,
    [formValues.brandId, formValues.categoryId, formValues.categoryName]
  );

  const categoryOptions = useMemo(
    () => (pendingCategory ? [pendingCategory, ...categories] : categories),
    [categories, pendingCategory]
  );

  const selectedCategory = useMemo(
    () =>
      pendingCategory ||
      categories.find((category) => category.category_id === formValues.categoryId) ||
      null,
    [categories, formValues.categoryId, pendingCategory]
  );

  const filterManufacturers = (_, { inputValue }) =>
    manufacturerChoices({ manufacturers, inputValue, pendingManufacturer });

  const filterBrands = (_, { inputValue }) =>
    brandChoices({
      brands,
      manufacturers,
      manufacturerId: formValues.manufacturerId,
      manufacturerIsPending: Boolean(pendingManufacturer),
      inputValue,
      pendingBrand,
    });

  const filterCategories = (_, { inputValue }) =>
    categoryChoices({
      categories,
      brandId: formValues.brandId,
      brandIsPending: Boolean(pendingBrand),
      inputValue,
      pendingCategory,
    });

  // Pending values and add options have no id; they match by name.
  const sameOption = (idKey, nameKey) => (option, value) =>
    option.pending || value.pending || option.inputValue || value.inputValue
      ? option[nameKey] === value[nameKey]
      : option[idKey] === value[idKey];

  const creatableProps = {
    freeSolo: true,
    autoHighlight: true,
    selectOnFocus: true,
    clearOnBlur: true,
    handleHomeEndKeys: true,
  };

  const selectedDistributor = useMemo(
    () =>
      distributors.find(
        (distributor) => distributor.reseller_id === formValues.distributorId
      ) || null,
    [distributors, formValues.distributorId]
  );

  const previewImage = imagePreview || existingImage;

  useEffect(() => {
    if (!open) return;

    setImageFile(null);
    setImagePreview("");
    setFieldErrors({});
    setBarcodeTaken(false);
    setLookingUp(false);
    lastLookedUpBarcode.current = null;

    if (initialProduct) {
      setExistingImage(initialProduct.product_image || "");
      setFormValues(productRowToFormValues(initialProduct));
      return;
    }

    setExistingImage("");
    setFormValues(EMPTY_PRODUCT_FORM);
  }, [open, initialProduct]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const updateField = (field, value) => {
    if (field === "barcode") {
      setBarcodeTaken(false);
      lastLookedUpBarcode.current = null;
      // The picture on screen belonged to the previous code. A file the user
      // picked stays; the catalog picture is cleared until the next lookup.
      // Edit keeps the product's saved picture — this lookup is add-only.
      if (!isEdit && !imageFileRef.current) {
        setExistingImage("");
      }
    }
    const nextValue =
      field === "barcode" ? alphanumericBarcode(value) : value;
    setFormValues((current) => ({
      ...current,
      [field]: nextValue,
      // A dismissed catalog picture should not follow a different barcode.
      ...(field === "barcode" && !isEdit && !imageFileRef.current
        ? { imageDeleted: false }
        : {}),
    }));
    setFieldErrors((current) => {
      const fieldsToClear =
        field === "isRetailProduct" ? [field, "name", "msrp"] : [field];
      if (!fieldsToClear.some((fieldName) => current[fieldName])) return current;
      const next = { ...current };
      fieldsToClear.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });
  };

  const updateFields = (values, fieldsToClear) => {
    setFormValues((current) => ({ ...current, ...values }));
    setFieldErrors((current) => {
      const next = { ...current };
      fieldsToClear.forEach((field) => {
        delete next[field];
      });
      return next;
    });
  };

  /**
   * Master catalog lookup (JD-422). On leaving the barcode field of a new
   * product: "own" blocks the create (the product already exists here),
   * "master" pre-fills the empty fields and, when the catalog has a picture,
   * shows that picture. Anything else changes nothing.
   */
  const lookupBarcode = async () => {
    if (isEdit) return;
    const barcode = String(formValues.barcode ?? "").trim();
    if (!barcode || barcode === lastLookedUpBarcode.current) return;
    lastLookedUpBarcode.current = barcode;
    // The barcode may change, or the dialog close and reopen, while this
    // request is in flight; a response for a superseded barcode is dropped.
    const isCurrent = () => lastLookedUpBarcode.current === barcode;

    setLookingUp(true);
    try {
      const { data } = await baseAxios.get(
        `/products/master-lookup/${encodeURIComponent(barcode)}`
      );
      if (!isCurrent()) return;
      if (data?.status === "own") {
        setBarcodeTaken(true);
        setFieldErrors((current) => ({
          ...current,
          barcode: "You already have this product.",
        }));
      } else if (data?.status === "master") {
        const image = catalogImageUrl(data);
        setFormValues((current) => {
          const next = applyMasterLookup(current, data, {
            manufacturers,
            brands,
          });
          // A file the user already picked stays. Otherwise the catalog
          // picture (or the lack of one) is what the form will save.
          return imageFileRef.current ? next : { ...next, imageDeleted: false };
        });
        if (!imageFileRef.current) {
          setExistingImage(image ?? "");
        }
        setFieldErrors((current) => {
          const next = { ...current };
          ["name", "ozSize", "manufacturerId", "brandId"].forEach((field) => {
            delete next[field];
          });
          return next;
        });
      }
    } catch (error) {
      // 404 = in neither catalog: nothing to pre-fill. Anything else must not
      // block creating the product by hand, and may be retried on the next
      // blur of the same barcode.
      if (error?.response?.status !== 404) {
        console.warn("Master catalog lookup failed", error);
        if (isCurrent()) lastLookedUpBarcode.current = null;
      }
    } finally {
      if (isCurrent()) setLookingUp(false);
    }
  };

  // Letters and digits only, for both add and edit. Enter on a new product
  // looks the code up instead of submitting a form that is still incomplete.
  const handleBarcodeKeyDown = (event) => {
    if (event.key === "Enter") {
      if (isEdit) return;
      event.preventDefault();
      lookupBarcode();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length === 1 && !/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleBarcodePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") ?? "";
    const input = event.target;
    const start = input.selectionStart ?? String(input.value).length;
    const end = input.selectionEnd ?? String(input.value).length;
    const next = alphanumericBarcode(
      String(input.value).slice(0, start) + pasted + String(input.value).slice(end)
    );
    updateField("barcode", next);
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.isRetailProduct && !String(formValues.name ?? "").trim()) {
      errors.name = "Product name is required.";
    }
    if (needsManufacturer(formValues)) {
      errors.manufacturerId = "Manufacturer is required.";
    } else if (String(formValues.manufacturerName ?? "").trim().length > 80) {
      errors.manufacturerId = "Manufacturer name must be 80 characters or fewer.";
    }
    if (needsBrand(formValues)) {
      errors.brandId = "Brand is required.";
    } else if (String(formValues.brandName ?? "").trim().length > 64) {
      errors.brandId = "Brand name must be 64 characters or fewer.";
    }
    if (
      !hasValue(formValues.categoryId) &&
      String(formValues.categoryName ?? "").trim().length > 32
    ) {
      errors.categoryId = "Category name must be 32 characters or fewer.";
    }
    const barcode = String(formValues.barcode ?? "").trim();
    if (!barcode) {
      errors.barcode = "Barcode is required.";
    } else if (!/^[a-zA-Z0-9]+$/.test(barcode)) {
      errors.barcode = "Barcode must contain only letters and numbers.";
    } else if (barcodeTaken) {
      errors.barcode = "You already have this product.";
    }
    if (!hasValue(formValues.ozSize)) {
      errors.ozSize = "Size is required.";
    } else if (Number(formValues.ozSize) < 0) {
      errors.ozSize = "Size cannot be negative.";
    }
    if (!hasValue(formValues.price)) {
      errors.price = "Salon cost is required.";
    } else if (Number(formValues.price) < 0) {
      errors.price = "Salon cost cannot be negative.";
    }
    if (formValues.isRetailProduct && !hasValue(formValues.msrp)) {
      errors.msrp = "MSRP is required for retail products.";
    } else if (hasValue(formValues.msrp) && Number(formValues.msrp) < 0) {
      errors.msrp = "MSRP cannot be negative.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    setFormValues((current) => ({ ...current, imageDeleted: false }));
    event.target.value = "";
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setExistingImage("");
    setFormValues((current) => ({ ...current, imageDeleted: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();
      const data = {
        name: formValues.name,
        ...productRefsPayload(formValues),
        ...categoryRefPayload(formValues),
        barcode: formValues.barcode,
        ozSize: formValues.ozSize,
        price: formValues.price,
        msrp: formValues.msrp,
        imageDeleted: formValues.imageDeleted,
        productImage: existingImage || null,
        isRetailProduct: formValues.isRetailProduct || false,
        resellerId: formValues.distributorId,
      };

      if (imageFile) {
        payload.append("productImage", imageFile);
      }

      payload.append("data", JSON.stringify(data));

      if (isEdit) {
        await baseAxios.put(`products/${productId}`, payload);
        toast.success("Product updated successfully.", { duration: 2500 });
      } else {
        await baseAxios.post("products", payload);
        toast.success("Product created successfully.", { duration: 2500 });
      }

      onSaved();
      onClose();
    } catch (error) {
      const errorData = error?.response?.data || error?.data;
      if (errorData?.fieldErrors) {
        setFieldErrors(errorData.fieldErrors);
      } else if (errorData?.error) {
        toast.error(errorData.error, { duration: 3000 });
      } else if (errorData?.message) {
        toast.error(errorData.message, { duration: 3000 });
      } else {
        toast.error("Error saving product.", { duration: 3000 });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "92vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: colors.black,
          color: colors.white,
          px: 3,
          py: 1.75,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            {isEdit ? "Edit Product" : "Add Product"}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
            {isEdit
              ? "Update product details, pricing, and image."
              : "Create a new product with details, pricing, and image."}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close product dialog"
          onClick={onClose}
          disabled={busy}
          sx={{
            color: colors.gold,
            "&:hover": { bgcolor: "rgba(197,160,111,0.15)" },
          }}
        >
          <ClearIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#141416" : "grey.50",
        }}
      >
        {formOptionsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: colors.gold }} />
          </Box>
        ) : (
          <Box
            component="form"
            id="product-dialog-form"
            onSubmit={handleSubmit}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
              alignItems: "start",
              gap: 3,
              p: 3,
              "& .MuiFormLabel-asterisk": {
                color: "#c62828",
                fontWeight: 800,
              },
            }}
          >
            <Box
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 2,
                position: { md: "sticky" },
                top: 0,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: 230,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: (theme) =>
                    previewImage
                      ? theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.2)"
                        : "grey.300"
                      : "divider",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#1b1b1e" : "grey.50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  mb: !isEdit && !imageFile && existingImage ? 1 : 2,
                  cursor: previewImage ? "pointer" : "default",
                  "&:hover .product-dialog-image-overlay": {
                    opacity: 1,
                  },
                }}
                role={previewImage ? "button" : undefined}
                tabIndex={previewImage ? 0 : undefined}
                onClick={
                  previewImage
                    ? () =>
                        onImageOpen({
                          src: previewImage,
                          name: formValues.name || "Product",
                        })
                    : undefined
                }
                onKeyDown={
                  previewImage
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onImageOpen({
                            src: previewImage,
                            name: formValues.name || "Product",
                          });
                        }
                      }
                    : undefined
                }
              >
                {previewImage ? (
                  <>
                    <Box
                      component="img"
                      src={previewImage}
                      alt={formValues.name || "Product"}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        bgcolor: "background.paper",
                      }}
                    />
                    <Box
                      className="product-dialog-image-overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0,0,0,0.45)",
                        color: "white",
                        opacity: 0,
                        transition: "opacity 160ms ease",
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 34 }} />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", color: "text.secondary", px: 2 }}>
                    <ImageOutlinedIcon sx={{ fontSize: 46, color: "grey.400" }} />
                    <Typography variant="body2" fontWeight={700}>
                      No product image
                    </Typography>
                    <Typography variant="caption">Upload one for easy review</Typography>
                  </Box>
                )}
              </Box>
              {!isEdit && !imageFile && existingImage ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mb: 1.5 }}
                >
                  From the JD Tech catalog
                </Typography>
              ) : null}

              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={<UploadFileIcon />}
                disabled={busy}
                sx={{
                  borderRadius: CONTROL_RADIUS,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {previewImage ? "Replace Image" : "Upload Image"}
                <Box
                  component="input"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {previewImage ? (
                <Button
                  variant="text"
                  color="error"
                  fullWidth
                  startIcon={<DeleteOutlineIcon />}
                  disabled={busy}
                  onClick={handleRemoveImage}
                  sx={{
                    mt: 1,
                    borderRadius: CONTROL_RADIUS,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Remove Image
                </Button>
              ) : null}
            </Box>

            <Box
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 2.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.75,
                }}
              >
                <TextField
                  label="Barcode"
                  value={formValues.barcode}
                  onChange={(event) => updateField("barcode", event.target.value)}
                  onBlur={lookupBarcode}
                  onKeyDown={handleBarcodeKeyDown}
                  onPaste={handleBarcodePaste}
                  autoFocus={!isEdit}
                  required
                  error={Boolean(fieldErrors.barcode)}
                  helperText={
                    fieldErrors.barcode ||
                    (isEdit
                      ? "Letters and numbers only"
                      : "Letters and numbers only. Checked against the master catalog when you leave the field or press Enter.")
                  }
                  fullWidth
                  size="small"
                  sx={{ gridColumn: "1 / -1" }}
                  inputProps={{ pattern: "[A-Za-z0-9]*" }}
                  InputProps={{
                    endAdornment: lookingUp ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                <TextField
                  label="Product Name / Color Number"
                  value={formValues.name}
                  onChange={(event) => updateField("name", event.target.value)}
                  required={!formValues.isRetailProduct}
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name}
                  fullWidth
                  size="small"
                  sx={{ gridColumn: "1 / -1" }}
                />
                <Autocomplete
                  {...creatableProps}
                  options={manufacturerOptions}
                  value={selectedManufacturer}
                  filterOptions={filterManufacturers}
                  onChange={(_, value) => {
                    const picked = resolvePick(value, {
                      idKey: "manufacturer_id",
                      nameKey: "manufacturer_name",
                      rows: manufacturers,
                    });
                    if (
                      picked.id === formValues.manufacturerId &&
                      picked.name === formValues.manufacturerName
                    ) {
                      return;
                    }
                    updateFields(
                      {
                        manufacturerId: picked.id,
                        manufacturerName: picked.name,
                        brandId: null,
                        brandName: null,
                        categoryId: null,
                        categoryName: null,
                      },
                      ["manufacturerId", "brandId", "categoryId"]
                    );
                  }}
                  getOptionLabel={(option) =>
                    optionLabel(option, "manufacturer_name")
                  }
                  isOptionEqualToValue={sameOption(
                    "manufacturer_id",
                    "manufacturer_name"
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manufacturer"
                      required
                      size="small"
                      placeholder="Pick one or type a new name"
                      error={Boolean(fieldErrors.manufacturerId)}
                      helperText={fieldErrors.manufacturerId}
                    />
                  )}
                />
                <Autocomplete
                  {...creatableProps}
                  options={brandOptions}
                  value={selectedBrand}
                  disabled={needsManufacturer(formValues)}
                  filterOptions={filterBrands}
                  onChange={(_, value) => {
                    const picked = resolvePick(value, {
                      idKey: "brand_id",
                      nameKey: "brand_name",
                      rows: brands,
                    });
                    if (
                      picked.id === formValues.brandId &&
                      picked.name === formValues.brandName
                    ) {
                      return;
                    }
                    // The brand decides the manufacturer: an existing brand
                    // picked from under another manufacturer brings it along.
                    const switchManufacturer =
                      picked.row &&
                      picked.row.manufacturer_id !== formValues.manufacturerId;
                    updateFields(
                      {
                        brandId: picked.id,
                        brandName: picked.name,
                        categoryId: null,
                        categoryName: null,
                        ...(switchManufacturer
                          ? {
                              manufacturerId: picked.row.manufacturer_id,
                              manufacturerName: null,
                            }
                          : {}),
                      },
                      ["manufacturerId", "brandId", "categoryId"]
                    );
                  }}
                  getOptionLabel={(option) => optionLabel(option, "brand_name")}
                  isOptionEqualToValue={sameOption("brand_id", "brand_name")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Brand"
                      required
                      size="small"
                      placeholder="Pick one or type a new name"
                      error={Boolean(fieldErrors.brandId)}
                      helperText={fieldErrors.brandId}
                    />
                  )}
                />
                <Autocomplete
                  {...creatableProps}
                  options={categoryOptions}
                  value={selectedCategory}
                  disabled={needsBrand(formValues)}
                  filterOptions={filterCategories}
                  onChange={(_, value) => {
                    const picked = resolvePick(value, {
                      idKey: "category_id",
                      nameKey: "category_name",
                      rows: categories.filter(
                        (category) => category.brand_id === formValues.brandId
                      ),
                    });
                    updateFields(
                      { categoryId: picked.id, categoryName: picked.name },
                      ["categoryId"]
                    );
                  }}
                  getOptionLabel={(option) =>
                    optionLabel(option, "category_name")
                  }
                  isOptionEqualToValue={sameOption(
                    "category_id",
                    "category_name"
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      size="small"
                      placeholder="Pick one or type a new name"
                      error={Boolean(fieldErrors.categoryId)}
                      helperText={fieldErrors.categoryId}
                    />
                  )}
                />
                <Autocomplete
                  options={distributors}
                  value={selectedDistributor}
                  onChange={(_, value) =>
                    updateField("distributorId", value?.reseller_id ?? null)
                  }
                  getOptionLabel={(option) => option?.reseller_company_name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.reseller_id === value.reseller_id
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Distributor" size="small" />
                  )}
                />
                <TextField
                  label="Size (Oz.)"
                  type="number"
                  value={formValues.ozSize}
                  onChange={(event) => updateField("ozSize", event.target.value)}
                  required
                  error={Boolean(fieldErrors.ozSize)}
                  helperText={fieldErrors.ozSize}
                  size="small"
                  inputProps={{ min: 0, step: "any" }}
                />
                <TextField
                  label="Salon Cost"
                  type="number"
                  value={formValues.price}
                  onChange={(event) => updateField("price", event.target.value)}
                  required
                  error={Boolean(fieldErrors.price)}
                  helperText={fieldErrors.price}
                  size="small"
                  inputProps={{ min: 0, step: "any" }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <TextField
                  label="MSRP"
                  type="number"
                  value={formValues.msrp}
                  onChange={(event) => updateField("msrp", event.target.value)}
                  required={formValues.isRetailProduct}
                  error={Boolean(fieldErrors.msrp)}
                  helperText={fieldErrors.msrp}
                  size="small"
                  inputProps={{ min: 0, step: "any" }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.isRetailProduct}
                      onChange={(event) =>
                        updateField("isRetailProduct", event.target.checked)
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: colors.gold,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: colors.gold,
                          },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        Retail product
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sold directly to customers
                      </Typography>
                    </Box>
                  }
                  sx={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    m: 0,
                    px: 1.25,
                    py: 0.75,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{
            borderRadius: CONTROL_RADIUS,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="product-dialog-form"
          variant="contained"
          color="primary"
          disabled={busy || barcodeTaken}
          sx={{
            borderRadius: CONTROL_RADIUS,
            minWidth: 130,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Product"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ManageProducts() {
  const [isMissingInfo, setIsMissingInfo] = useState(false);
  const [products, setProducts] = useState([]);
  const [missingInfoProducts, setMissingInfoProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [sortBy, setSortBy] = useState("product_id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [manufacturerFilter, setManufacturerFilter] = useState([]);
  const [manufacturerFilterMenuAnchor, setManufacturerFilterMenuAnchor] =
    useState(null);
  const [brandFilter, setBrandFilter] = useState([]);
  const [brandFilterMenuAnchor, setBrandFilterMenuAnchor] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [categoryFilterMenuAnchor, setCategoryFilterMenuAnchor] =
    useState(null);
  const [retailFilter, setRetailFilter] = useState([]);
  const [retailFilterMenuAnchor, setRetailFilterMenuAnchor] = useState(null);
  const [typeFilter, setTypeFilter] = useState([]);
  const [typeFilterMenuAnchor, setTypeFilterMenuAnchor] = useState(null);
  const [distributorFilter, setDistributorFilter] = useState([]);
  const [distributorFilterMenuAnchor, setDistributorFilterMenuAnchor] =
    useState(null);
  const [imageFilter, setImageFilter] = useState([]);
  const [imageFilterMenuAnchor, setImageFilterMenuAnchor] = useState(null);
  const [imageViewer, setImageViewer] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [dialogProductId, setDialogProductId] = useState(null);
  const [dialogInitialProduct, setDialogInitialProduct] = useState(null);
  const [formBrands, setFormBrands] = useState([]);
  const [formManufacturers, setFormManufacturers] = useState([]);
  const [formCategories, setFormCategories] = useState([]);
  const [formDistributors, setFormDistributors] = useState([]);
  const [formOptionsLoading, setFormOptionsLoading] = useState(true);

  const openProduct = useCallback((row) => {
    setDialogProductId(row.product_id);
    setDialogInitialProduct(row);
    setProductDialogOpen(true);
  }, []);

  const handleAddNewProduct = useCallback(() => {
    setDialogProductId(null);
    setDialogInitialProduct(null);
    setProductDialogOpen(true);
  }, []);

  const closeProductDialog = useCallback(() => {
    setProductDialogOpen(false);
    setDialogProductId(null);
    setDialogInitialProduct(null);
  }, []);

  const loadProducts = useCallback(() => {
    setLoading(true);
    Promise.all([baseAxios.get("/products"), baseAxios.get("/products/missingInfo")])
      .then(([productsResult, missingInfoResult]) => {
        setProducts((productsResult.data?.data ?? []).map(formatProduct));
        setMissingInfoProducts(
          (missingInfoResult.data?.data ?? []).map(formatProduct)
        );
      })
      .catch((error) => {
        setProducts([]);
        setMissingInfoProducts([]);
        toast.error(
          error.status === 403
            ? "Not allowed to see this data"
            : "Error loading data",
          { duration: 3000 }
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const loadFormOptions = useCallback(() => {
    setFormOptionsLoading(true);
    Promise.all([
      baseAxios.get("/products/brands/all"),
      baseAxios.get("/products/manufacturers/all"),
      baseAxios.get("/products/categories/all"),
      baseAxios.get("/resellers"),
    ])
      .then(([resBrands, resManufacturers, resCategories, resDistributors]) => {
        // The API already sorts these (JD-426); sorting here too keeps the
        // pickers alphabetical against an older API.
        setFormBrands(sortByName(resBrands.data, "brand_name"));
        setFormManufacturers(
          sortByName(resManufacturers.data, "manufacturer_name")
        );
        setFormCategories(sortByName(resCategories.data, "category_name"));

        const distributorsList = Array.isArray(resDistributors.data)
          ? resDistributors.data
          : resDistributors.data.resellers || [];
        setFormDistributors(
          distributorsList.filter((distributor) => distributor.is_active === true)
        );
      })
      .catch(() => {
        toast.error("Error loading product form options.", { duration: 3000 });
      })
      .finally(() => setFormOptionsLoading(false));
  }, []);

  useEffect(() => {
    loadFormOptions();
  }, [loadFormOptions]);

  // Saving a product (JD-422, JD-423) or importing a file (JD-425) may have
  // created manufacturers, brands or categories; the pickers must show them,
  // or an imported product opens for editing with those fields blank.
  const handleProductSaved = useCallback(() => {
    loadProducts();
    loadFormOptions();
  }, [loadProducts, loadFormOptions]);

  const activeProducts = isMissingInfo ? missingInfoProducts : products;

  const filterOptions = useMemo(() => {
    const brands = new Set();
    const categories = new Set();
    const types = new Set();
    const distributors = new Set();
    activeProducts.forEach((product) => {
      if (product.brand) brands.add(product.brand);
      if (product.category) categories.add(product.category);
      if (product.type) types.add(product.type);
      if (product.distributor) distributors.add(product.distributor);
    });

    const sortOptions = (values) =>
      [...values].sort((a, b) =>
        String(a)
          .trim()
          .localeCompare(String(b).trim(), undefined, { sensitivity: "base" })
      );

    return {
      manufacturers: manufacturerFilterOptions(
        formManufacturers,
        activeProducts.map((product) => product.manufacturer)
      ),
      brands: sortOptions(brands),
      categories: sortOptions(categories),
      types: sortOptions(types),
      distributors: sortOptions(distributors),
    };
  }, [activeProducts, formManufacturers]);

  const filteredProducts = useMemo(
    () =>
      activeProducts.filter((product) => {
        if (!productMatchesSearch(product, deferredSearch)) return false;
        if (
          manufacturerFilter.length > 0 &&
          !manufacturerFilter.some(
            (selected) =>
              String(selected).trim().toLowerCase() ===
              String(product.manufacturer ?? "").trim().toLowerCase()
          )
        ) {
          return false;
        }
        if (brandFilter.length > 0 && !brandFilter.includes(product.brand)) {
          return false;
        }
        if (
          categoryFilter.length > 0 &&
          !categoryFilter.includes(product.category)
        ) {
          return false;
        }
        if (typeFilter.length > 0 && !typeFilter.includes(product.type)) {
          return false;
        }
        if (
          distributorFilter.length > 0 &&
          !distributorFilter.includes(product.distributor)
        ) {
          return false;
        }
        if (
          retailFilter.length > 0 &&
          !retailFilter.includes(product.isRetail)
        ) {
          return false;
        }
        if (imageFilter.length > 0) {
          const hasImage = Boolean(product.product_image);
          if (!imageFilter.includes(hasImage)) return false;
        }
        return true;
      }),
    [
      activeProducts,
      brandFilter,
      categoryFilter,
      deferredSearch,
      distributorFilter,
      imageFilter,
      manufacturerFilter,
      retailFilter,
      typeFilter,
    ]
  );

  const sortedProducts = useMemo(() => {
    const copy = [...filteredProducts];
    copy.sort((a, b) => {
      const result = compareProductRows(a, b, sortBy);
      return sortOrder === "asc" ? result : -result;
    });
    return copy;
  }, [filteredProducts, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const clearAllFilters = useCallback(() => {
    setManufacturerFilter([]);
    setBrandFilter([]);
    setCategoryFilter([]);
    setTypeFilter([]);
    setDistributorFilter([]);
    setRetailFilter([]);
    setImageFilter([]);
    setSearchInput("");
  }, []);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    manufacturerFilter.forEach((v) =>
      chips.push({
        key: `mfg-${v}`,
        label: `Manufacturer: ${v}`,
        onDelete: () =>
          setManufacturerFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    brandFilter.forEach((v) =>
      chips.push({
        key: `brand-${v}`,
        label: `Brand: ${v}`,
        onDelete: () => setBrandFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    categoryFilter.forEach((v) =>
      chips.push({
        key: `cat-${v}`,
        label: `Category: ${v}`,
        onDelete: () =>
          setCategoryFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    typeFilter.forEach((v) =>
      chips.push({
        key: `type-${v}`,
        label: `Type: ${v}`,
        onDelete: () => setTypeFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    distributorFilter.forEach((v) =>
      chips.push({
        key: `dist-${v}`,
        label: `Distributor: ${v}`,
        onDelete: () =>
          setDistributorFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    retailFilter.forEach((v) =>
      chips.push({
        key: `retail-${v}`,
        label: `Retail: ${v ? "Yes" : "No"}`,
        onDelete: () => setRetailFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    imageFilter.forEach((v) =>
      chips.push({
        key: `img-${v}`,
        label: v ? "Has image" : "Missing image",
        onDelete: () => setImageFilter((prev) => prev.filter((x) => x !== v)),
      })
    );
    return chips;
  }, [
    brandFilter,
    categoryFilter,
    distributorFilter,
    imageFilter,
    manufacturerFilter,
    retailFilter,
    typeFilter,
  ]);

  const handleExport = useCallback(async () => {
    if (!sortedProducts.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Products");
      worksheet.columns = [
        { header: "Product", key: "name", width: 28 },
        { header: "Barcode", key: "barcode", width: 16 },
        { header: "Manufacturer", key: "manufacturer", width: 18 },
        { header: "Brand", key: "brand", width: 16 },
        { header: "Category", key: "category", width: 16 },
        { header: "Type", key: "type", width: 14 },
        { header: "Distributor", key: "distributor", width: 18 },
        { header: "Oz. Size", key: "ozWeight", width: 10 },
        { header: "Salon Cost", key: "price", width: 12 },
        { header: "MSRP", key: "msrp", width: 12 },
        { header: "Retail", key: "isRetail", width: 10 },
        { header: "Has Image", key: "hasImage", width: 10 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedProducts.forEach((row) => {
        worksheet.addRow({
          name: row.name || "",
          barcode: row.barcode || "",
          manufacturer: row.manufacturer || "",
          brand: row.brand || "",
          category: row.category || "",
          type: row.type || "",
          distributor: row.distributor || "",
          ozWeight: row.ozWeight || "",
          price: row.price || "",
          msrp: row.msrp || "",
          isRetail: row.isRetail ? "Yes" : "No",
          hasImage: row.product_image ? "Yes" : "No",
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Products_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedProducts]);

  const columns = useMemo(() => {
    const searchQuery = deferredSearch;
    return [
      {
        id: "product",
        label: "Product",
        field: "name",
        width: "18%",
        filtered: imageFilter.length > 0,
        headerExtras: (
          <RetailFilter
            value={imageFilter}
            onChange={setImageFilter}
            anchorEl={imageFilterMenuAnchor}
            setAnchorEl={setImageFilterMenuAnchor}
            ariaLabel="Filter by product image"
            yesLabel="Has image"
            noLabel="Missing image"
          />
        ),
        render: (_, row) => (
          <ProductCell
            row={row}
            query={searchQuery}
            onImageOpen={setImageViewer}
          />
        ),
      },
      {
        id: "manufacturer",
        label: "Manufacturer",
        field: "manufacturer",
        width: "10%",
        filtered: manufacturerFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={manufacturerFilter}
            onChange={setManufacturerFilter}
            anchorEl={manufacturerFilterMenuAnchor}
            setAnchorEl={setManufacturerFilterMenuAnchor}
            options={filterOptions.manufacturers}
            ariaLabel="Filter by manufacturer"
            allLabel="All manufacturers"
          />
        ),
        render: (value) =>
          textCell(
            value ? <HighlightText text={value} query={searchQuery} /> : "-"
          ),
      },
      {
        id: "brand",
        label: "Brand",
        field: "brand",
        width: "9%",
        filtered: brandFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={brandFilter}
            onChange={setBrandFilter}
            anchorEl={brandFilterMenuAnchor}
            setAnchorEl={setBrandFilterMenuAnchor}
            options={filterOptions.brands}
            ariaLabel="Filter by brand"
            allLabel="All brands"
          />
        ),
        render: (value) =>
          textCell(
            value ? <HighlightText text={value} query={searchQuery} /> : "-"
          ),
      },
      {
        id: "category",
        label: "Category",
        field: "category",
        width: "9%",
        filtered: categoryFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
            anchorEl={categoryFilterMenuAnchor}
            setAnchorEl={setCategoryFilterMenuAnchor}
            options={filterOptions.categories}
            ariaLabel="Filter by category"
            allLabel="All categories"
          />
        ),
        render: (value) =>
          textCell(
            value ? <HighlightText text={value} query={searchQuery} /> : "-"
          ),
      },
      {
        id: "type",
        label: "Type",
        field: "type",
        width: "8%",
        filtered: typeFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={typeFilter}
            onChange={setTypeFilter}
            anchorEl={typeFilterMenuAnchor}
            setAnchorEl={setTypeFilterMenuAnchor}
            options={filterOptions.types}
            ariaLabel="Filter by product type"
            allLabel="All types"
          />
        ),
        render: (value) =>
          value ? (
            <Chip
              size="small"
              label={<HighlightText text={value} query={searchQuery} />}
              variant="outlined"
              sx={{
                fontWeight: 600,
                borderRadius: CONTROL_RADIUS,
                borderColor: colors.divider,
              }}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "distributor",
        label: "Distributor",
        field: "distributor",
        width: "10%",
        filtered: distributorFilter.length > 0,
        headerExtras: (
          <FieldFilter
            value={distributorFilter}
            onChange={setDistributorFilter}
            anchorEl={distributorFilterMenuAnchor}
            setAnchorEl={setDistributorFilterMenuAnchor}
            options={filterOptions.distributors}
            ariaLabel="Filter by distributor"
            allLabel="All distributors"
          />
        ),
        render: (value) =>
          textCell(
            value ? <HighlightText text={value} query={searchQuery} /> : "-"
          ),
      },
      {
        id: "ozWeight",
        label: "Oz. Size",
        field: "ozWeight",
        width: "6%",
        render: (value) =>
          value ? <HighlightText text={String(value)} query={searchQuery} /> : "-",
      },
      {
        id: "price",
        label: "Salon Cost",
        field: "price",
        width: "7%",
        render: (value) => (
          <Typography component="span" fontWeight={600} fontSize={14}>
            {value ? (
              <HighlightText text={String(value)} query={searchQuery} />
            ) : (
              "-"
            )}
          </Typography>
        ),
      },
      {
        id: "msrp",
        label: "MSRP",
        field: "msrp",
        width: "7%",
        render: (value) => (
          <Typography component="span" fontWeight={600} fontSize={14}>
            {value ? (
              <HighlightText text={String(value)} query={searchQuery} />
            ) : (
              "-"
            )}
          </Typography>
        ),
      },
      {
        id: "isRetail",
        label: "Retail",
        field: "isRetail",
        width: "7%",
        filtered: retailFilter.length > 0,
        headerExtras: (
          <RetailFilter
            value={retailFilter}
            onChange={setRetailFilter}
            anchorEl={retailFilterMenuAnchor}
            setAnchorEl={setRetailFilterMenuAnchor}
          />
        ),
        render: (value) => (
          <Chip
            size="small"
            label={value ? "Yes" : "No"}
            variant={value ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
              bgcolor: value ? colors.gold : "transparent",
              color: value ? colors.black : "text.secondary",
              borderColor: value ? colors.gold : "divider",
            }}
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        width: "9%",
        render: (_, row) => (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openProduct(row)}
            sx={{
              borderRadius: CONTROL_RADIUS,
              minHeight: 28,
              py: 0.25,
              px: 1.25,
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              "& .MuiButton-startIcon": { mr: 0.5 },
            }}
          >
            Edit
          </Button>
        ),
      },
    ];
  }, [
    brandFilter,
    brandFilterMenuAnchor,
    categoryFilter,
    categoryFilterMenuAnchor,
    deferredSearch,
    distributorFilter,
    distributorFilterMenuAnchor,
    filterOptions,
    imageFilter,
    imageFilterMenuAnchor,
    manufacturerFilter,
    manufacturerFilterMenuAnchor,
    openProduct,
    retailFilter,
    retailFilterMenuAnchor,
    typeFilter,
    typeFilterMenuAnchor,
  ]);

  return (
    <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isMissingInfo
              ? "Showing products that need setup details."
              : "View, search, filter, and edit product details and images."}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <ProductViewSwitcher
            isMissingInfo={isMissingInfo}
            productsCount={products.length}
            missingInfoCount={missingInfoProducts.length}
            onChange={setIsMissingInfo}
          />
          <TextField
            size="small"
            placeholder="Search product, brand, category, barcode, image…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            variant="outlined"
            sx={{
              minWidth: 300,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: CONTROL_RADIUS,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "grey.400" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh products"
            onClick={loadProducts}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: CONTROL_RADIUS,
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !sortedProducts.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadFileIcon />}
            onClick={() => setImportDialogOpen(true)}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddImagesIcon />}
            onClick={() => setImagesDialogOpen(true)}
            disabled={loading || !products.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Add images
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNewProduct}
            sx={{
              borderRadius: CONTROL_RADIUS,
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} /> Add
          </Button>
        </Box>
      </Box>

      {activeFilterChips.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          {activeFilterChips.map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              label={chip.label}
              onDelete={chip.onDelete}
              sx={{
                borderRadius: CONTROL_RADIUS,
                bgcolor: colors.black,
                color: colors.gold,
                fontWeight: 600,
                "& .MuiChip-deleteIcon": {
                  color: colors.gold,
                  "&:hover": { color: colors.white },
                },
              }}
            />
          ))}
          <Button
            size="small"
            onClick={clearAllFilters}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              minHeight: 28,
            }}
          >
            Clear filters
          </Button>
        </Box>
      ) : null}

      <PaginatedTable
        columns={columns}
        rows={sortedProducts}
        totalRowCount={sortedProducts.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="product_id"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${isMissingInfo}|${manufacturerFilter.join(",")}|${brandFilter.join(",")}|${categoryFilter.join(",")}|${retailFilter.join(",")}|${typeFilter.join(",")}|${distributorFilter.join(",")}|${imageFilter.join(",")}`}
        clientSidePagination
        skeletonHeight="28px"
        getRowKey={(row) => row.product_id}
        fitWidth
        emptyMessage={
          isMissingInfo
            ? "No products need setup details"
            : "No products found"
        }
      />
      <ProductDialog
        open={productDialogOpen}
        productId={dialogProductId}
        initialProduct={dialogInitialProduct}
        brands={formBrands}
        manufacturers={formManufacturers}
        categories={formCategories}
        distributors={formDistributors}
        formOptionsLoading={formOptionsLoading}
        onClose={closeProductDialog}
        onSaved={handleProductSaved}
        onImageOpen={setImageViewer}
      />
      <ImportProductsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={handleProductSaved}
      />
      <ProductImagesDialog
        open={imagesDialogOpen}
        onClose={() => setImagesDialogOpen(false)}
        products={products}
        onImagesChanged={loadProducts}
      />
      <Modal
        open={Boolean(imageViewer)}
        onClose={() => setImageViewer(null)}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.82)" },
          },
        }}
      >
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            outline: "none",
          }}
          onClick={() => setImageViewer(null)}
        >
          <IconButton
            aria-label="Close image viewer"
            onClick={() => setImageViewer(null)}
            sx={{
              position: "fixed",
              top: 18,
              right: 18,
              bgcolor: "rgba(255,255,255,0.12)",
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            }}
          >
            <ClearIcon />
          </IconButton>
          <Box
            sx={{
              maxWidth: "min(92vw, 1200px)",
              maxHeight: "88vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Box
              component="img"
              src={imageViewer?.src}
              alt={imageViewer?.name || "Product"}
              sx={{
                maxWidth: "100%",
                maxHeight: "82vh",
                objectFit: "contain",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            />
            {imageViewer?.name ? (
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  bgcolor: "rgba(0,0,0,0.35)",
                  borderRadius: CONTROL_RADIUS,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                {imageViewer.name}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
